import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Calendar } from '../../../../web/components-v2/Calendar';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Field, FieldError, FieldLabel } from '../../../../web/components-v2/Field';
import { TimeInput } from '../../../../web/components-v2/TimeInput';
import { styleSheet } from '../styles';

const CLOCK_REFRESH_INTERVAL_MS = 15_000;

interface IProps {
	initialValue?: Date;
	isOpen: boolean;
	onCancel: () => void;
	onSave: (endDate: Date) => void;
}

export const CustomDurationPicker: React.FC<IProps> = ({ initialValue, isOpen, onCancel, onSave }) => {
	const [selectedDate, setSelectedDate] = useState<Date>(initialValue || moment().add(1, 'day').toDate());
	const [isTimeEmpty, setIsTimeEmpty] = useState(false);
	const [selectedTime, setSelectedTime] = useState<moment.Moment>(
		moment(initialValue || moment().add(1, 'day').toDate())
	);

	const handleDateChange = (date: Date) => {
		setSelectedDate(date);
		// Update time to maintain the selected time
		const updatedTime = moment(date).set({
			hour: selectedTime.hour(),
			minute: selectedTime.minute(),
		});
		setSelectedTime(updatedTime);
	};

	const handleTimeChange = (value: string) => {
		if (value === '') {
			setIsTimeEmpty(true);
			return;
		}
		const parsed = moment(value, 'HH:mm', true);
		if (!parsed.isValid()) {
			return;
		}
		setIsTimeEmpty(false);
		setSelectedTime(parsed);
		const updatedDate = moment(selectedDate).set({
			hour: parsed.hour(),
			minute: parsed.minute(),
		});
		setSelectedDate(updatedDate.toDate());
	};

	const finalDate = moment(selectedDate).set({
		hour: selectedTime.hour(),
		minute: selectedTime.minute(),
		second: 0,
		millisecond: 0,
	});
	const [now, setNow] = useState(() => moment());
	const isInPast = finalDate.isSameOrBefore(now);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		setNow(moment());
		const timer = setInterval(() => setNow(moment()), CLOCK_REFRESH_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [isOpen]);

	const handleSave = () => {
		const current = moment();
		if (finalDate.isSameOrBefore(current)) {
			setNow(current);
			return;
		}
		onSave(finalDate.toDate());
	};

	const handleCancel = () => {
		setIsTimeEmpty(false);
		onCancel();
	};

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleCancel()}>
			<DialogPopup forceBackdrop>
				<DialogHeader>
					<DialogTitle>Select End Date and Time</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<div className={css(styleSheet.dialogColumn)}>
						<Field styles={[styleSheet.field]} role='group' aria-labelledby='custom-duration-date-label'>
							<FieldLabel id='custom-duration-date-label'>Date:</FieldLabel>
							<Calendar
								mode='single'
								selected={selectedDate}
								defaultMonth={selectedDate}
								onSelect={date => date && handleDateChange(date)}
								disabled={{ before: new Date() }}
								showOutsideDays={false}
							/>
						</Field>
						<Field styles={[styleSheet.field]} role='group' aria-labelledby='custom-duration-time-label'>
							<FieldLabel id='custom-duration-time-label'>Time:</FieldLabel>
							<TimeInput value={isTimeEmpty ? '' : selectedTime.format('HH:mm')} onChange={handleTimeChange} />
							{!isTimeEmpty && isInPast && <FieldError>Choose an end date and time in the future.</FieldError>}
						</Field>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button onClick={handleSave} disabled={isTimeEmpty || isInPast}>
						Save
					</Button>
					<Button variant='secondary' onClick={handleCancel}>
						Cancel
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
